(ns web3.i18n
  (:refer-clojure :exclude [get])
  (:require [web3.log :as log]
            [web3.utils :as utils]
            ["messageformat" :as MessageFormat]
            [clojure.string :as str]
            [web3.env :as env]))

(def ^js formatter (MessageFormat. "en"))

(.addFormatters
  formatter
  #js {:default
       (fn [the-var locale default]
         ;; Adds a `default` formatter for specifying fallback values.
         ;; Usage:
         ;; "Hello, {name, default, friend}"
         ;;                         ^^ 'friend' will be used if 'name' is not present.
         (if (utils/some-str the-var)
           the-var
           default))
       :when-exists
       (fn [the-var locale the-string]
         (let [the-string (if (string? the-string) the-string (.join the-string ","))]
           (if (utils/some-str the-var)
             (-> the-string
                 (str/replace "#" the-var)
                 (str/replace "~(COMMA)" ",")
                 (str/replace "~(SPACE)" " "))
             "")))})

(def compile (memoize (fn [s] (.compile formatter s))))

(defn format
  "Format a string in ICU format"
  [s data]
  ((compile s) (clj->js data)))

(comment
  ;; example usage: when-exists
  (let [s "a{tagName, when-exists, Brand:, '12#34'}b"]
    (format s {:tagName "X"}))
  (let [s "I'm looking for '{missingType}' content. {tagName, when-exists, Type: '#'~(COMMA) a}"]
    (format s {:tagName "TAG"
               :missingType "TYPE"}))
  (let [s "Hello{name, when-exists, {abc}}"]
    (format s {:name "Frank"})))

(defn parse-collection [c]
  (let [total (count c)]
    {:count total
     :count-rest (- total 2)
     :first (first c)
     :second (second c)
     :third (first (nnext c))}))

(declare get)

(def locales
  "Map of translations. All user-facing text should be defined here.

   Entries should be plain strings with ICU-style interpolation. In the
   future, these translations will probably be moved to external files
   using a standard i18n file format (that is why we do not support
   arbitrary Clojure functions to construct strings).

   See the `aliases` map below for function-based dynamic key lookup."
  {:auth/sign-up-terms {:en "I agree to the <a href='https://www.homeserve.com/en-us/app/terms' target='_new'>terms of service</a> and <a href='https://www.homeserve.com/sc/legal/privacy-policy' target='_new'>privacy policy</a>."}

   :autocomplete/no-options-found {:en "No options found."}

   :nav/welcome-title {:en "Welcome Home"}

   :i/group-name {:en "Group Name"}

   :i/my {:en "My"}
   :i/optional {:en "optional"}
   :i/processing {:en "Processing"}

   :asset/alert-status.processing.title
   {:en "We are working on your {tagName}"}
   :asset/alert-status.processing.detail
   {:en "We’re busy finding the manuals, guides, how-to videos and any parts or accessories for it. This can take several hours ({processingTimeframe}) or in some cases more for difficult items. We will let you know when it’s ready."}

   :asset/alert-status.errored.title
   {:en "Can you help with this one?"}
   :asset/alert-status.errored.detail
   {:en "We could not identify what product this is. Please give us a bit more information."}
   :i/clarify {:en "Clarify"}
   :asset/asset-clarify.invalid.empty
   {:en "Oops, the form is empty! Please add information to help us figure out exactly what this item is."}

   :asset/alert-status.recalled.title
   {:en "This {tagName} may be recalled."}

   ;;TODO
   :i/my-groups {:en "Property Groups"} ;; These string used to prefixed by my,
   :i/my-photos-videos {:en "Photo / Video"} ;; need to combine with identical
   :i/my-documents {:en "Document"} ;; strings elsewhere in this namespace
   :i/my-notes {:en "Note"}
   :content/how-tos {:en "How-To Videos"}
   :i/how-tos {:en "How-Tos"}
   :i/continue {:en "Continue"}

   :i/professional-profile {:en "Professional Profile"}

   ;; HomeServe policy link label
   :drawer/homeserve-policy {:en "My Homeserve Plans"}


   :not-found/content.inquiry {:en "Hi {partnerName}, \nCan you help me find {missingType} for Type: {tagName, default, unknown}, Brand: {manufacturer, default, unknown}, Model: {modelNumber, default, unknown}."}
   :not-found/products.inquiry {:en "Hi {partnerName}, \nCan you help me find parts for Brand: {manufacturer, default, unknown}, Model: {modelNumber, default, unknown}, Type: {tagName, default, unknown}\nSpecifically, I'm looking for:\n---please add specific part---\nPlease add a photo, if possible."}

   :did-we-get-it-right/chat.inquiry {:en (str "Hi {partnerName}, \n\nI’m looking at my:\n "
                                               "Brand: {manufacturer, default, unknown}\n"
                                               "Model: {modelNumber, default, unknown}\n"
                                               "Type: {tagName, default, unknown}"
                                               "\n and...")}
   :did-we-get-it-right/thumbs-down.inquiry {:en (str "Hi {partnerName}, \n\nSomething seems wrong with my:\n"
                                                      "Brand: {manufacturer, default, unknown}\n"
                                                      "Model: {modelNumber, default, unknown}\n"
                                                      "Type: {tagName, default, unknown}"
                                                      " \nThe issue is:")}
   :i/export {:en "Export my Inventory"}
   :drawer/export-intercom {:en "Hi {partnerName}. I would like report of everything I have added in the app. \n {email} \n {uuid}"}

   :drawer/need-help.title {:en "Need Help?"}
   :drawer/need-help.info {:en "Let us help you find a qualified professional in your area."}
   :drawer/need-help.label {:en "Find a Pro"}
   :i/service-providers {:en "Service Providers"}
   :i/service-provider-details {:en "Service Provider Details"}
   :i/add-service-provider {:en "Add Service Provider"}
   :i/emergency-info {:en "Emergency Info"}
   :i/notifications {:en "Notifications"}
   :i/help {:en "Help"}
   :i/logout {:en "Sign Out"}
   :i/logoImage {:en "Company Logo"}
   :i/profileImage {:en "Profile Picture"}
   :drawer/link.contact {:en "Chat With Us"}
   :drawer/link.terms {:en "Terms of Use"}
   :i/privacy {:en "Privacy Policy"}
   :i/start {:en "Start"}
   :welcome/start-intro {:en "Let’s start by setting up your first property"}
   :drawer/link.quick-start {:en "Quick Start Guide"}
   :drawer/link.faq {:en "Frequently Asked Questions"}
   :drawer/link.nameplate-help {:en "How To Find a Product Label"}
   :i/nameplate {:en "Product Label"}
   :i/copy-link {:en "Copy Link"}

   :i/account {:en "Account"}
   :i/contact-us {:en "Contact Us"}
   :i/rate-our-app {:en "Rate Our App"}
   :i/share-centriq {:en "Share HomeServe"}
   :i/social {:en "Find Us on Social Media"}
   :i/edit-profile {:en "Edit Profile"}
   :i/change-password {:en "Change Password"}

   :i/facebook {:en  "Like {partnerName, default, HomeServe} on Facebook"}

   :i/instagram {:en "Follow {partnerName, default, HomeServe} on Instagram"}

   :i/twitter {:en "Follow {partnerName, default, HomeServe} on Twitter"}

   :i/linkedin {:en "Follow {partnerName, default, HomeServe} on LinkedIn"}

   :i/property-details {:en "Property Details"}
   :i/edit-property {:en "Edit Property"}
   :i/my-properties {:en "My Properties"}
   :i/other-properties {:en "Other Properties"}

   :forms/validators.required {:en "{label, default, Field} is required."}
   :forms/validators.min-length {:en "{label, default, Text} is too short (minimum {n} characters)"}

   :forms/validators.max-length {:en "{label, default, Text} is too long (max {n} characters)"}
   :forms/validators.confirm-password {:en "Passwords must match"}
   :forms/validators.postalCode {:en "Must be a valid U.S. or Canadian postal code"}

   :i/firstName {:en "First Name"}
   :i/lastName {:en "Last Name"}
   :i/email {:en "Email"}
   :i/password {:en "Password"}
   :i/current-password {:en "Current Password"}
   :i/new-password {:en "New Password"}
   :i/confirm-password {:en "Confirm Password"}

   :i/street1 {:en "Street Address"}
   :i/street2 {:en "Address 2"}
   :i/locality {:en "City"}
   :i/region {:en "State/Province"}
   :i/postalCode {:en "ZIP/Postal Code"}
   :i/zipCode {:en "ZIP code"}
   :i/country {:en "Country"}
   :i/address {:en "Address"}
   :i/skip {:en "Skip"}
   :i/next {:en "Next"}

   :property/default-nickname {:en "My Home in {city}"}
   :i/contact-information {:en "Contact Information"}

   :i/companyName {:en "Company Name"}


   :i/homePhone {:en "Home"}
   :i/mobilePhone {:en "Mobile"}
   :i/officePhone {:en "Office"}
   :i/otherPhone {:en "Other"}
   :i/iphonePhone {:en "iPhone"}

   :i/professionName {:en "Profession"}
   :i/website {:en "Website"}

   :StreetAddress/country.us {:en "United States"}
   :StreetAddress/country.ca {:en "Canada"}

   :Asset/assetGroupId {:en "Group"}
   :Asset/tagName {:en "What is this?"}
   :Asset/nameplateLink {:en "Product Label Photo"}
   :i/photo {:en "Photo"}

   :i/nickname {:en "Display Name"}
   :property/nickname {:en "Property Display Name"}

   :group/delete-complete {:en "Deleted group."}
   :property/delete-complete {:en "Deleted property."}
   :asset/delete-complete {:en "Deleted asset."}
   :content/delete-complete {:en "Deleted content."}
   :service-provider/delete-complete {:en "Deleted service provider."}

   :property/delete.primary {:en "Are you sure you want to delete the property \"{nickname}\"?"}
   :group/delete.primary {:en "Are you sure you want to delete the group \"{name}\"?"}

   :property/delete.secondary {:en "This will delete all groups and their items and files."}
   :group/delete.secondary {:en "Any items or content within the group will also be deleted."}

   :i/delete.confirm-contents.primary {:en "Are you sure you want to delete \"{name}\" and all its content?"}
   :i/delete.confirm-contents.secondary
   {:en "This will delete {count, plural,
                                            =1 {\"{first}\"}
                                            =2 {\"{first}\" and \"{second}\"}
                                            =3 {\"{first}\", \"{second}\" and \"{third}\"}
                                            other {\"{first}\", \"{second}\", and {count-rest} other items and files}}."}

   :property/delete.confirm-contents.secondary
   {:en "This will permanently delete {count-groups, plural,
                                        =1 {1 group}
                                        other {# groups}}, {count-assets, plural,
                                        =1 {1 item}
                                        other {# items}}, and {count-contents, plural,
                                        =1 {1 user-added file}
                                        other {# user-added photos, videos, and documents}}."}
   :i/delete-type.primary {:en "Are you sure you want to delete this {type}?"}
   :asset/delete.primary {:en "Are you sure you want to delete the item \"{name}\"?"}
   :service-provider/delete.primary {:en "Are you sure you want to delete this entry?"}
   :i/are-you-sure {:en "Are you sure?"}

   :modal/delete.info {:en "This cannot be undone."}

   :account/email.changed {:en "Email Changed"}
   :account/email.changed.continue {:en "Please log back in with your new email: { email }."}
   :account/change.password.wrong-password {:en "Current password doesn't match."}
   :account/change.password.too-weak {:en "Password is too weak."}
   :account/password.changed {:en "Password Changed"}

   :i/delete {:en "Delete"}
   :i/cancel {:en "Cancel"}
   :i/create {:en "Create"}
   :i/change {:en "Change"}
   :i/done {:en "Done"}
   :i/ok {:en "Ok"}

   :i/learn-more {:en "Learn More"}
   :i/duplicate-property {:en "Duplicate Property"}
   :i/transfer-property {:en "Transfer Property"}
   :i/transfer {:en "Transfer"}
   :i/transferring {:en "Transferring"}
   :i/to {:en "To"}
   :i/duplicate {:en "Duplicate"}
   :i/duplicate-as {:en "Duplicate As..."}
   :i/transfer-to {:en "Transfer To"}
   :i/accept-transfer {:en "Accept Transfer"}
   :i/send-from {:en "Send From"}

   :transfer/confirmation-message {:en "{firstName} will receive an email with an invitation to add the property to an existing or new account.\n\nYou can continue to access the property until {firstName} accepts the invitation."}
   :transfer/in-progress-message {:en "This property has been sent to {firstName} {lastName} ({email}) and is pending acceptance."}
   :i/transfer-property-withdraw {:en "Withdraw Transfer"}

   :i/personImageLink {:en "Profile Image"}
   :i/companyImageLink {:en "Company Logo"}

   :pro/get-account {:en "Get a Pro Account"}
   :i/centriq-pro {:en "HomeServe Pro"}

   :i/type {:en "Type"}

   :i/home {:en "Home"}

   :i/add-new-property {:en "Add New Property"}
   :i/add-new-service-provider {:en "Add New Service Provider"}
   :i/add-group {:en "Add Group"}

   :vendor/powered-by {:en "Powered By"}
   :vendor/toh.ask {:en "Ask This Old House"}

   :i/loading {:en "Loading"}
   :error/server-500 {:en "Oops! We're experiencing some technical difficulties."}
   :i/file {:en "File"}
   :i/files {:en "Files"}
   :i/my-files {:en "Files"}
   :i/items {:en "Items"}
   :i/my-items {:en "Item"}
   :i/my-receipts {:en "Receipt"}

   :files/manuals {:en "Manuals & Guides"}

   :i/add-notes {:en "Add Note"}
   :i/add-documents {:en "Add Document"}
   :i/add-receipts {:en "Add Receipt"}
   :i/add-media {:en "Add Photo/Video"}
   :i/add-manuals {:en "Add Document"}
   :i/add-property {:en "Add Property"}
   :i/new-transfer {:en "New Transfer"}
   :i/create-group {:en "Create Group"}

   :i/receipt {:en "Receipt"}
   :i/receipts {:en "Receipts"}

   :i/document {:en "Document"}
   :i/documents {:en "Documents"}

   :i/note {:en "Note"}
   :i/notes {:en "Notes"}

   :i/photos-videos {:en "Photos & Videos"}

   :i/date {:en "Date"}
   :i/delete-file {:en "Delete File"}
   :i/add {:en "Add"}
   :i/add-another {:en "Add Another"}

   :i/add-anything {:en "Add Anything"}
   :i/add-item {:en "Add Item"}
   :i/add-file {:en "Add File"}
   :i/upload-file {:en "Upload File"}
   :i/this-group {:en "This Group"}
   :i/this-property {:en "This Property"}
   :select-parent/search-label {:en "search..."}
   :add/by-nameplate {:en "Use Product Label Photo"}
   :i/type-manually {:en "Type Manually"}
   :add/group.submit {:en "Create"}
   :i/add-items {:en "Add items"}
   :i/add-files {:en "Add files"}
   :i/add-other {:en "Add other"}
   :add/nameplate-help.link {:en "What is a product label?"}
   :add/nameplate-help.link.href {:en "https://www.homeserve.com/en-us/app/help/"}
   :add/nameplate-locator.description {:en "A product label shows the brand, model, and serial number and can be found somewhere on your item."}
   :i/nameplate-locator {:en "Product Label Locator"}
   :add/nameplate-locator.link.href {:en "https://myhome.homeserve.com/nameplate/"}
   :add/end.success {:en "Success! You've added "}
   :add/end.view-item {:en "View item"}

   :i/other {:en "Other"}
   :forms/choose-item {:en "Choose {label}..."}

   :modal/select-parent {:en "Add To"}

   ;; parent is always referring to a field
   :i/add-to {:en "Add to..."}

   :i/manufacturer {:en "Brand"}
   :i/manufacturerName {:en "Brand"} ;; For graphql Compat

   :i/model {:en "Model"}
   :i/serialNumber {:en "Serial Number"}
   :i/modelNumber {:en "Model"}
   :i/subcategory {:en "Subcategory"}

   :model/is-this-your {:en "Is this your {asset, default, item}?"}

   :i/faq {:en "FAQ"}
   :nameplate-locator/title {:en "Where's my Product Label?"}

   :files.upload.messages/default {:en "Drop file here or click to upload."}
   :files.upload.messages/mobile {:en "Tap here to add a photo"}
   :files.upload.messages/replace {:en "Drop or click to replace."}
   :files.upload.error/file-size {:en "The file size is too big ({{ value }} max)."}
   :files.upload.error/min-width {:en "The image width is too small ({{ value }}}px min)."}
   :files.upload.error/max-width {:en "The image width is too big ({{ value }}}px max)."}
   :files.upload.error/min-height {:en "The image height is too small ({{ value }}}px max)."}
   :files.upload.error/max-height {:en "The image height is too big ({{ value }}}px max)."}
   :files.upload.error/image-format {:en "The image format is not allowed ({{ value }} only)."}
   :i/remove {:en "Remove"}
   :i/text-me {:en "Text me the App"}

   :asset/products {:en "Parts & Accessories"}

   :i/file-details {:en "File Details"}
   :i/title {:en "Title"}
   :i/description {:en "Description"}

   :i/body {:en "Body"}
   :i/comments {:en "Comments"}
   :asset/transcription-comment-helptext {:en "Add any comments that might help identify the product."}

   :i/service-provider {:en "Service Provider"}
   :i/asset-group {:en "Group"}
   :i/Reminder {:en "Reminder"}
   :i/property {:en "Property"}
   :i/Asset {:en "Item"}
   :i/Model {:en "Model"}
   :i/image {:en "Image"}
   :i/images {:en "Photos"}
   :i/video {:en "Video"}
   :i/videos {:en "Videos"}
   :files/add-unknown {:en "Add..."}
   :i/brought-to-you-by {:en ""}
   :i/create-your-property {:en "Create Your Property"}

   :i/edit-pro-profile {:en "Edit Professional Profile"}

   :i/dashboard {:en "Dashboard"}

   :i/category {:en "Category"}

   :i/product-info {:en "Product Info"}
   :i/send {:en "Send"}
   :i/saved {:en "Saved"}

   :errors/unknown {:en "Oops! An error has occurred."}

   :not-found/videos {:en "No videos found."}
   :not-found/products {:en "No parts found."}
   :not-found/manuals {:en "No manuals found."}
   :not-found/ask {:en "Can't find what you're looking for? Send us a message."}

   :recall/resolved {:en "Recall Resolved"}
   :recall/rejected {:en "Recall Did Not Apply"}
   :recall/dismissed {:en "I live dangerously"}
   :recall/did-apply? {:en "\"Did the recall apply to your item?\""}
   :recall/did-apply?.detail {:en "Was the recall addressed, or did it not apply to your item?"}
   :i/take-action {:en "Take Action"}
   :i/dismiss {:en "Dismiss"}
   :recall/dismiss-title {:en "Dismiss Recall Notice"}
   :i/reset {:en "Reset"}
   :i/not-found {:en "Not Found"}

   :transcription-error/not-a-nameplate
   {:en "We could not detect a product label in the photo you submitted. If the item has a product label, please add a close-up photo or enter the brand and model below. Othewise simply dismiss this message. If you'd like help, let us know."}

   :transcription-error/model-missing
   {:en "The product label photo you submitted doesn't appear to contain the model number. See if there is another label that shows the model number and submit a close-up photo of it, or enter it manually below."}

   :transcription-error/no-brand-or-model
   {:en "The product label photo you submitted appears to only show the serial number. See if there is another label that contains the brand and model number and take a close-up photo of it, or enter those details manually below."}

   :transcription-error/no-serial
   {:en "We identified your item but could not identify the serial number for your item. The serial number helps us be more specific with recall data, and lets us estimate the age of your item which might come in handy with a warranty. If you know the serial number, enter it below."}

   :transcription-error/missing-brand
   {:en "The product label photo you submitted doesn't appear to contain the brand name. See if there is another label that shows the brand name and submit a close-up photo of it, or enter it manually below."}

   :transcription-error/model-series
   {:en "The product label photo you submitted has a model number that is used for a series of products, not a single model. See if there is another label that contains the specific model number, and if possible the serial number as well, or enter it manually below."}

   :transcription-error/wrong-nameplate
   {:en "The product label photo you submitted doesn't appear to have the information we need to identify the item.  See if there is another label that contains the brand and model and potentially the serial number and submit a close-up photo of it, or enter it manually below."}

   :transcription-error/model-subcomponent
   {:en "The product label photo you submitted has a model number that is associated with a particular part or component instread of the whole product. This can happen when somthing is attached to the main item. See if there is another label that containts the primary item's model number and if possible the serial number, or enter it manually below."}

   :transcription-error/indentification-problems
   {:en "We had trouble identifying your item. Please add a comment with a web link to where we can find it online. You can also update the image to show the brand and model number more clearly."}

   :transcription-error/nameplate-conflicting
   {:en "The product label you submitted is detected as a valid nameplate, but it cannot be matched with certainty. Please enter the brand and model number below."}

   :transcription-error/no-matching-brand-and-model
   {:en "We could not find a product that matched the brand and model you entered. Please verify that information is correct, or submit a clear photo of the product label that contains the brand and model number."}
   :i/my-account {:en "My Account "}
   :transcription-error/bad-photo
   {:en "The product label photo you submitted could not be read. It might have been blurry, too dark or over exposed, might not have contained the entire label, or maybe the label was too small. Please take another close-up photo that is clearly legible, or enter the details manually below."}})

(def rejectionCode->key
  (utils/flatten-keys
    {["0" "11" "N2"] :transcription-error/not-a-nameplate
     ["1" "2" "N6"] :transcription-error/model-missing
     ["3" "N9"] :transcription-error/no-brand-or-model
     "4" :transcription-error/no-serial
     "N5" :transcription-error/missing-brand
     ["5" "N7"] :transcription-error/model-series
     ["6" "N3"] :transcription-error/wrong-nameplate
     ["7" "8" "N8"] :transcription-error/model-subcomponent
     "9" :transcription-error/indentification-problems
     "N10" :transcription-error/nameplate-conflicting
     "10" :transcription-error/no-matching-brand-and-model
     ;; code 11 is the first key in the list
     ["12" "N4"] :transcription-error/bad-photo}))


(def aliases
  "Lookup table for i18n keywords, used by `resolve-key`.

   Value should be a valid i18n keyword, or a function which returns a valid i18n keyword.

   The objective of this map is to allow for some indirection via aliases,
   while maintaining a simple/dumb i18n map as the external translation/localization interface."
  {:add.file/category (fn [{:keys [category]}]
                        (keyword "files.add" category))
   :type/detail (fn [{:keys [__typename]}]
                  (case __typename
                    "Content" :i/file-details
                    (keyword "i" __typename)))
   :i/asset :i/Asset
   :i/parent :i/add-to

   :files.upload.messages/error :errors/unknown

   :files/media :i/my-photos-videos
   :files/image :i/images
   :files/video :i/videos
   :files/note :i/notes
   :files/document :i/documents
   :files/receipt :i/receipts
   :files/item :i/items
   :files/receipts :i/my-receipts
   :files/documents :i/my-documents
   :files/notes :i/my-notes
   :files/items :i/my-items
   :files.add/unknown :i/add-item

   :files.add/notes :i/add-notes
   :files.add/documents :i/add-documents
   :files.add/receipts :i/add-receipts
   :files.add/media :i/add-media
   :files.add/manuals :i/add-manuals

   :type/Property :i/property
   :type/AssetGroup :i/asset-group
   :type/ServiceProvider :i/service-provider


   :not-found/manuals.inquiry :not-found/content.inquiry
   :not-found/videos.inquiry :not-found/content.inquiry

   :modal/delete.primary :i/are-you-sure})

(defn resolve-key
  "Resolve key via the `aliases` map."
  ([key]
   (assert (and (keyword? key) (namespace key))
           key)
   (let [k (clojure.core/get aliases key key)]
     (if (fn? k) (k) k)))
  ([key params]
   (assert (and (keyword? key) (namespace key))
           key)
   (let [k (clojure.core/get aliases key key)]
     (if (fn? k) (k params) k))))

(def locale :en)

(defn get
  "Return translation for `key`, which may be either a keyword or vector of keywords
   (which will be concatenated).

   Namespaced keywords will backoff to the name of the keyword.

  Provide fallback `key` or string."
  ([key]
   (or (clojure.core/get-in locales [(resolve-key key) locale])
       ;; if namespaced keyword is not found, back off to :i/ namespaced key
       (or (when (and (keyword? key) (some-> (namespace key)
                                             (not= "i")))
             (try (get (keyword "i" (name key)))
                  (catch :default e
                    nil)))
           (throw (js/Error (str "i18n key not found: " key))))))
  ([key params]
   ;; if params are supplied, we expect the key to resolve to a function
   (try
     (let [s (get (resolve-key key params))]
       (format s (or params #js {})))
     (catch js/Error e
       (log/println :error "Error localizing key: " {:key key
                                                     :params params})
       #_(rf/dispatch [:events/handle-error
                       {:source ::get
                        :error (js/Error. (str "Error localizing key: " key))
                        :debug-log {:key key
                                    :params params}}])))))

(comment
  (get :not-found/content.inquiry {:missingType "Parts & Accessories"
                                   :tagName "Blender"
                                   :manufacturer "Bosch"
                                   :modelNumber "123"}))
(def to-do
  "Placeholder for translations that need to be named/moved into this namespace"
  identity)